import { useState, useEffect } from 'react';
import { Plus, Search, Pencil, Trash2, Loader2, X, RefreshCw } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { formatDate } from '@/lib/dateFormat';
import { DateInput } from '@/components/ui/DateInput';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SelectField } from '@/components/ui/SelectField';

type CampaignStatus = 'Active' | 'Paused' | 'Completed';
type Provider = 'Facebook' | 'Google' | 'LinkedIn' | 'Instagram' | 'WhatsApp' | 'Email' | 'Other';

interface Campaign {
  id: string;
  name: string;
  provider: Provider;
  status: CampaignStatus;
  isActive: boolean;
  startDate?: string | null;
  endDate?: string | null;
  budget?: number | null;
  description?: string | null;
  createdAt: string;
}

const STATUS_COLORS: Record<CampaignStatus, string> = {
  Active: 'bg-emerald-50 text-emerald-700',
  Paused: 'bg-amber-50 text-amber-700',
  Completed: 'bg-slate-100 text-slate-500',
};

const PROVIDERS: Provider[] = ['Facebook', 'Google', 'LinkedIn', 'Instagram', 'WhatsApp', 'Email', 'Other'];
const STATUSES: CampaignStatus[] = ['Active', 'Paused', 'Completed'];

// ── Campaign Drawer ─────────────────────────────────────────────────────────────
function CampaignDrawer({ campaign, onClose, onSaved }: {
  campaign?: Campaign; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!campaign;
  const [form, setForm] = useState({
    name: campaign?.name ?? '',
    provider: campaign?.provider ?? ('Facebook' as Provider),
    status: campaign?.status ?? ('Active' as CampaignStatus),
    startDate: campaign?.startDate?.slice(0, 10) ?? '',
    endDate: campaign?.endDate?.slice(0, 10) ?? '',
    budget: campaign?.budget != null ? String(campaign.budget) : '',
    description: campaign?.description ?? '',
    isActive: campaign?.isActive ?? true,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = {
      name: form.name,
      provider: form.provider,
      status: form.status,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      budget: form.budget ? parseFloat(form.budget) : null,
      description: form.description || null,
      isActive: form.isActive,
    };
    try {
      if (isEdit) await api.patch(`/campaigns/${campaign!.id}`, payload);
      else await api.post('/campaigns', payload);
      toast('success', isEdit ? 'Campaign updated' : 'Campaign created');
      onSaved();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent placeholder:text-slate-400';
  const selectCls = `${inputCls} bg-white`;
  const labelCls = 'block text-xs font-medium text-slate-600 mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl flex flex-col h-full animate-in slide-in-from-right duration-300">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">{isEdit ? 'Edit Campaign' : 'New Campaign'}</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className={labelCls}>Campaign Name <span className="text-red-500">*</span></label>
            <input className={inputCls} placeholder="Enter campaign name" value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>Provider</label>
            <SelectField className={selectCls} value={form.provider}
              onChange={e => setForm(p => ({ ...p, provider: e.target.value as Provider }))}>
              {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
            </SelectField>
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <SelectField className={selectCls} value={form.status}
              onChange={e => setForm(p => ({ ...p, status: e.target.value as CampaignStatus }))}>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </SelectField>
          </div>

          {/* Campaign Duration */}
          <div>
            <label className={labelCls}>Campaign Duration</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="block text-[11px] text-slate-400 mb-1">Start Date</span>
                <DateInput value={form.startDate} onChange={v => setForm(p => ({ ...p, startDate: v }))}
                  placeholder="Start date" className="h-10" />
              </div>
              <div>
                <span className="block text-[11px] text-slate-400 mb-1">End Date</span>
                <DateInput value={form.endDate} onChange={v => setForm(p => ({ ...p, endDate: v }))}
                  placeholder="End date" className="h-10" />
              </div>
            </div>
          </div>

          <div>
            <label className={labelCls}>Budget (optional)</label>
            <input type="number" className={inputCls} placeholder="e.g. 5000" value={form.budget}
              onChange={e => setForm(p => ({ ...p, budget: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>Description (optional)</label>
            <textarea rows={3} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] resize-none placeholder:text-slate-400"
              placeholder="Campaign description" value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="flex items-center justify-between">
            <label className={labelCls + ' mb-0'}>Active</label>
            <button type="button" onClick={() => setForm(p => ({ ...p, isActive: !p.isActive }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isActive ? 'bg-[#6366F1]' : 'bg-gray-200'}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 border border-gray-200 hover:border-gray-300">Cancel</button>
          <button onClick={save} disabled={saving || !form.name.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Campaign'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Campaigns Page ──────────────────────────────────────────────────────────────
export function CampaignsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [drawer, setDrawer] = useState<{ open: boolean; campaign?: Campaign }>({ open: false });
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmCampaign, setConfirmCampaign] = useState<Campaign | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const { data } = await api.get(`/campaigns?${params}`);
      setCampaigns(Array.isArray(data) ? data : data.campaigns ?? data.data ?? []);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load campaigns');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchCampaigns(); }, []);

  const handleDelete = (c: Campaign) => setConfirmCampaign(c);

  const handleConfirmDelete = async () => {
    if (!confirmCampaign) return;
    setConfirmLoading(true);
    setDeletingId(confirmCampaign.id);
    try {
      await api.delete(`/campaigns/${confirmCampaign.id}`);
      fetchCampaigns();
      toast('success', 'Campaign deleted');
      setConfirmCampaign(null);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete');
    } finally {
      setConfirmLoading(false);
      setDeletingId(null);
    }
  };

  const toggleActive = async (c: Campaign) => {
    setTogglingId(c.id);
    try {
      await api.patch(`/campaigns/${c.id}`, { isActive: !c.isActive });
      setCampaigns(prev => prev.map(x => x.id === c.id ? { ...x, isActive: !x.isActive } : x));
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingId(null);
    }
  };

  const filtered = campaigns.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.provider.toLowerCase().includes(search.toLowerCase())
  );
  const { sorted, sort, onSort } = useSort(filtered);

  return (
    <DashboardLayout title="Campaigns">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Campaigns</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage marketing campaigns</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchCampaigns}
              className="flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 text-slate-500 hover:text-[#6366F1] hover:border-[#6366F1] transition-colors">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={() => setDrawer({ open: true })}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold transition-colors">
              <Plus className="h-4 w-4" /> Add Campaign
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchCampaigns()}
              placeholder="Search campaigns..."
              className="w-full h-10 pl-9 pr-4 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-[#6366F1]" />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-50">
            <span className="text-xs text-slate-400">{loading ? 'Loading...' : `${filtered.length} campaign${filtered.length !== 1 ? 's' : ''}`}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <TableHeadRow>
                  <SortTh label="S.No" sortable={false} />
                  <SortTh label="Campaign Name" sortKey="name" sort={sort} onSort={onSort} />
                  <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                  <SortTh label="Active" sortKey="isActive" sort={sort} onSort={onSort} />
                  <SortTh label="Dates" sortKey="startDate" sort={sort} onSort={onSort} />
                  <SortTh label="Actions" sortable={false} />
                </TableHeadRow>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#6366F1] mx-auto" /></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-16 text-center text-slate-400 text-sm">No campaigns found.</td></tr>
                ) : sorted.map((c, idx) => (
                  <tr key={c.id} className="hover:bg-gray-50/80 transition-colors">
                    <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => setDrawer({ open: true, campaign: c })}
                        className="font-medium text-[#6366F1] hover:underline text-left">
                        {c.name}
                      </button>
                      <p className="text-xs text-slate-400 mt-0.5">{c.provider}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[c.status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {togglingId === c.id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                      ) : (
                        <button onClick={() => toggleActive(c)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${c.isActive ? 'bg-[#6366F1]' : 'bg-gray-200'}`}>
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${c.isActive ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      <div className="text-xs">
                        {c.startDate ? formatDate(c.startDate) : '—'} <span className="text-slate-300 mx-1">to</span> {c.endDate ? formatDate(c.endDate) : '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-0.5">
                        <button onClick={() => setDrawer({ open: true, campaign: c })} className="p-1.5 rounded hover:bg-indigo-50 text-slate-400 hover:text-indigo-600">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {deletingId === c.id ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
                          <button onClick={() => handleDelete(c)} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {drawer.open && (
        <CampaignDrawer campaign={drawer.campaign}
          onClose={() => setDrawer({ open: false })}
          onSaved={() => { setDrawer({ open: false }); fetchCampaigns(); }} />
      )}

      <ConfirmModal
        isOpen={!!confirmCampaign}
        title="Delete Campaign?"
        message={`Are you sure you want to delete "${confirmCampaign?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmCampaign(null)}
        isLoading={confirmLoading}
      />
    </DashboardLayout>
  );
}
